import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { isEmpty, isNumber, pick, uniqueId } from 'lodash';

import { SearchInstanceDto } from '@app/components';
import {
  queryKeys,
  useAppLocale,
  useHoldsAndCheckoutsPageConfig,
  usePatronSession,
} from '@app/hooks';
import {
  getCheckoutsStatusesData,
  getFeesStatusesData,
  getRequestsStatusesData,
  makeRequest,
  setPageRecords,
} from '@app/utils';
import {
  ChargeDto,
  CheckoutDto,
  defaultRecordsPerPage,
  HoldDto,
  holdsAndCheckoutsTabs,
  ItemStatusData,
  SetState,
} from '@app/constants';

import { QueryConfig, SetTabsCounters } from '../constants';

interface HoldsAndCheckoutsDto {
  holds: HoldDto[];
  charges: ChargeDto[];
  loans: CheckoutDto[];
  totalHolds: number;
  totalChargesCount: number;
  totalLoans: number;
  sortByParams: SortBy;
}

interface HoldsAndCheckoutsData {
  pages: Partial<SearchInstanceDto>[][] | null;
  itemsStatusesData: ItemStatusData[] | null;
  pageNumber: number;
  holdsAndCheckoutsQuery: UseQueryResult<HoldsAndCheckoutsDto>;
  setPageNumber: SetState<number>;
}

interface SortBy {
  sortField: string;
  sortDirection: string;
}

type ItemsData = HoldDto[] | CheckoutDto[] | ChargeDto[];

export const useHoldsAndCheckoutsFetch = ({
  sortByParams,
  queryConfig,
  setTabsCounters,
}: {
  sortByParams: SortBy;
  queryConfig: QueryConfig;
  setTabsCounters: SetTabsCounters;
}): HoldsAndCheckoutsData => {
  const intl = useIntl();
  const location = useLocation();
  const { locale } = useAppLocale();
  const [pageNumber, setPageNumber] = useState(1);
  const [pages, setPages] = useState<Partial<SearchInstanceDto>[][]>([]);
  const [itemsStatusesData, setItemsStatusesData] = useState<
    ItemStatusData[] | null
  >(null);
  const { externalSystemId } = usePatronSession();
  const { getTabRoute, selectConfiguredOption } =
    useHoldsAndCheckoutsPageConfig();

  const setPagesData = useCallback(
    (itemsData: ItemsData) => {
      const { $t } = intl;
      const startInstancesIndex = (pageNumber - 1) * defaultRecordsPerPage;

      const formattedInstancesData = itemsData.map(record => {
        const item = record.item;

        if (!item && isEmpty(item)) {
          return {
            id: uniqueId(record.reason),
            title: record.reason
              ? record.reason
              : $t({
                  id: 'fees.noReason',
                  defaultMessage: 'Undefined',
                }),
          };
        }

        return {
          ...pick(item, 'title', 'publication', 'isbns', 'sourceTypes'),
          id: item.instanceId,
          contributors: item.authors?.map(author => ({ name: author })),
        };
      });
      const records = formattedInstancesData.slice(
        startInstancesIndex,
        startInstancesIndex + defaultRecordsPerPage
      );

      setPageRecords({ records, pageNumber, setPages });
    },
    [intl, pageNumber]
  );

  const holdsAndCheckoutsQuery = useQuery<HoldsAndCheckoutsDto>(
    [
      queryKeys.holdsAndCheckoutsPage,
      externalSystemId,
      sortByParams,
      queryConfig,
    ],
    () =>
      makeRequest<HoldsAndCheckoutsDto>({
        url: `/opac-patron/account/${externalSystemId}/with-rich-metadata`,
        params: {
          ...queryConfig,
          sortBy: `${sortByParams.sortField}/sort.${sortByParams.sortDirection}`,
        },
      }),
    { enabled: !isEmpty(externalSystemId) }
  );

  useEffect(() => {
    if (holdsAndCheckoutsQuery.data && !isEmpty(holdsAndCheckoutsQuery.data)) {
      const holdsData = holdsAndCheckoutsQuery.data.holds;
      const checkoutsData = holdsAndCheckoutsQuery.data.loans;
      const chargesData = holdsAndCheckoutsQuery.data.charges;

      if (location.pathname === getTabRoute(holdsAndCheckoutsTabs.holds)) {
        if (!isEmpty(holdsData)) {
          setPagesData(holdsData);
          setItemsStatusesData(
            getRequestsStatusesData(holdsData, intl, selectConfiguredOption)
          );
        } else {
          setItemsStatusesData([]);
        }
      }

      if (location.pathname === getTabRoute(holdsAndCheckoutsTabs.checkouts)) {
        if (!isEmpty(checkoutsData)) {
          setPagesData(checkoutsData);
          setItemsStatusesData(
            getCheckoutsStatusesData(checkoutsData, chargesData, intl, locale)
          );
        } else {
          setItemsStatusesData([]);
        }
      }

      if (location.pathname === getTabRoute(holdsAndCheckoutsTabs.fees)) {
        if (!isEmpty(chargesData)) {
          setPagesData(chargesData);
          setItemsStatusesData(
            getFeesStatusesData(chargesData, checkoutsData, intl, locale)
          );
        } else {
          setItemsStatusesData([]);
        }
      }
    }

    if (
      isEmpty(holdsAndCheckoutsQuery.data) &&
      holdsAndCheckoutsQuery.isSuccess
    ) {
      setItemsStatusesData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    holdsAndCheckoutsQuery.data,
    holdsAndCheckoutsQuery.isSuccess,
    locale,
    location,
    setPagesData,
  ]);

  useEffect(() => {
    if (holdsAndCheckoutsQuery.data) {
      const tabsCounters = {
        holds: holdsAndCheckoutsQuery.data.totalHolds,
        checkouts: holdsAndCheckoutsQuery.data.totalLoans,
        fees: holdsAndCheckoutsQuery.data.totalChargesCount,
      };

      if (Object.values(tabsCounters).every(isNumber)) {
        setTabsCounters(tabsCounters);
      }
    }
  }, [setTabsCounters, holdsAndCheckoutsQuery.data]);

  return {
    pages,
    itemsStatusesData,
    pageNumber,
    holdsAndCheckoutsQuery,
    setPageNumber,
  };
};
